import { DELTA, OPPOSITE, type Cell, type Dir, type Game, type GameEvent, type GameInput } from './types'

const DIRS: Dir[] = ['up', 'down', 'left', 'right']

/** Classic snake on a 32x18 board. The demo AI path-finds to the food and avoids boxing itself in. */
export class Snake implements Game {
	readonly name = 'snake'
	readonly W = 32
	readonly H = 18
	score = 0
	over = false
	private body: [number, number][] = [[8, 9], [7, 9], [6, 9]]
	private dir: Dir = 'right'
	private food: [number, number] = [20, 9]
	private grow = 0
	private ticks = 0
	private started = false

	get tickMs(): number {
		return Math.max(55, 120 - Math.floor(this.score / 30) * 8)
	}

	constructor() {
		this.placeFood()
	}

	step(input: GameInput, demo: boolean): GameEvent[] {
		this.ticks++
		// in play mode nothing moves until the first steer, so the board isn't a wall-run into instant death
		if (!demo && !this.started) {
			if (!input.pressed) return []
			this.started = true
		}
		const want = demo ? this.ai() : input.pressed
		if (want && want !== OPPOSITE[this.dir]) this.dir = want
		const [hx, hy] = this.body[0]
		const [dx, dy] = DELTA[this.dir]
		const nx = hx + dx
		const ny = hy + dy
		if (!this.free(nx, ny, this.grow === 0)) {
			this.over = true
			return ['die']
		}
		const events: GameEvent[] = []
		this.body.unshift([nx, ny])
		if (nx === this.food[0] && ny === this.food[1]) {
			this.score += 10
			this.grow += 2
			events.push('eat')
			this.placeFood()
		}
		if (this.grow > 0) this.grow--
		else this.body.pop()
		return events
	}

	cells(): Cell[] {
		const out: Cell[] = []
		const n = this.body.length
		this.body.forEach(([x, y], i) => {
			// head is white-green, the tail fades toward cyan
			const t = i / Math.max(1, n - 1)
			const r = Math.round(0x7c * (1 - t) + 0x22 * t)
			const g = Math.round(0xff * (1 - t) + 0xf0 * t)
			const b = Math.round(0x5a * (1 - t) + 0xff * t)
			out.push({ x, y, color: i === 0 ? 0xeaffe6 : (r << 16) | (g << 8) | b })
		})
		out.push({ x: this.food[0], y: this.food[1], color: this.ticks % 6 < 3 ? 0xff2bd6 : 0xffd84a })
		return out
	}

	// ---- internals ----

	private free(x: number, y: number, tailMoves: boolean): boolean {
		if (x < 0 || y < 0 || x >= this.W || y >= this.H) return false
		const last = tailMoves ? this.body.length - 1 : this.body.length
		for (let i = 0; i < last; i++) if (this.body[i][0] === x && this.body[i][1] === y) return false
		return true
	}

	private placeFood(): void {
		for (let tries = 0; tries < 500; tries++) {
			const x = Math.floor(Math.random() * this.W)
			const y = Math.floor(Math.random() * this.H)
			if (this.free(x, y, false)) {
				this.food = [x, y]
				return
			}
		}
		this.over = true
	}

	/** BFS from head to food; if no path, take the direction with the most reachable space. */
	private ai(): Dir {
		const [hx, hy] = this.body[0]
		const blocked = new Uint8Array(this.W * this.H)
		const last = this.grow === 0 ? this.body.length - 1 : this.body.length
		for (let i = 0; i < last; i++) blocked[this.body[i][1] * this.W + this.body[i][0]] = 1
		const start = hy * this.W + hx
		const goal = this.food[1] * this.W + this.food[0]
		const prev = new Int32Array(this.W * this.H).fill(-1)
		const queue = [start]
		prev[start] = start
		let found = false
		while (queue.length) {
			const cur = queue.shift()!
			if (cur === goal) { found = true; break }
			const cx = cur % this.W
			const cy = (cur - cx) / this.W
			for (const d of DIRS) {
				const nx = cx + DELTA[d][0]
				const ny = cy + DELTA[d][1]
				if (nx < 0 || ny < 0 || nx >= this.W || ny >= this.H) continue
				const ni = ny * this.W + nx
				if (blocked[ni] || prev[ni] !== -1) continue
				prev[ni] = cur
				queue.push(ni)
			}
		}
		if (found) {
			let cur = goal
			while (prev[cur] !== start) cur = prev[cur]
			return this.dirTo(hx, hy, cur % this.W, Math.floor(cur / this.W))
		}
		// no route: survive by heading for the roomiest neighbour
		let best: Dir = this.dir
		let bestSpace = -1
		for (const d of DIRS) {
			if (d === OPPOSITE[this.dir]) continue
			const nx = hx + DELTA[d][0]
			const ny = hy + DELTA[d][1]
			if (nx < 0 || ny < 0 || nx >= this.W || ny >= this.H || blocked[ny * this.W + nx]) continue
			const space = this.flood(nx, ny, blocked)
			if (space > bestSpace) { bestSpace = space; best = d }
		}
		return best
	}

	private flood(x: number, y: number, blocked: Uint8Array): number {
		const seen = new Uint8Array(this.W * this.H)
		const stack = [y * this.W + x]
		seen[stack[0]] = 1
		let count = 0
		while (stack.length && count < 400) {
			const cur = stack.pop()!
			count++
			const cx = cur % this.W
			const cy = (cur - cx) / this.W
			for (const d of DIRS) {
				const nx = cx + DELTA[d][0]
				const ny = cy + DELTA[d][1]
				if (nx < 0 || ny < 0 || nx >= this.W || ny >= this.H) continue
				const ni = ny * this.W + nx
				if (blocked[ni] || seen[ni]) continue
				seen[ni] = 1
				stack.push(ni)
			}
		}
		return count
	}

	private dirTo(x: number, y: number, tx: number, ty: number): Dir {
		if (tx > x) return 'right'
		if (tx < x) return 'left'
		if (ty > y) return 'down'
		return 'up'
	}
}
